/**
 * Mirrors backend role normalization (stepflow_core.rbac).
 *
 * For **current-workspace** UI (build, admin, usage), prefer `workspaceRole` from AuthContext
 * (membership from GET /auth/workspaces). `user.stepflowRole` comes from the ID token and can lag
 * after an admin changes your workspace role until tokens refresh.
 */

export function normalizeStepflowRole(raw) {
  const r = String(raw ?? '').trim().toLowerCase();
  if (!r) return 'USER';
  if (r === 'owner' || r === 'root') return 'OWNER';
  if (r === 'admin' || r === 'administrator') return 'ADMIN';
  if (r === 'creator' || r === 'create') return 'CREATOR';
  if (r === 'user' || r === 'operator') return 'USER';
  return 'USER';
}

export function canAccessWorkbenchBuild(role) {
  const r = normalizeStepflowRole(role);
  return r === 'OWNER' || r === 'ADMIN' || r === 'CREATOR';
}

/**
 * Network Workbench (`/network/*`) and Usage — OWNER, ADMIN, CREATOR (not USER).
 * Workspace admin (settings / members) is separate: {@link canAccessWorkspaceAdmin} (OWNER + ADMIN only).
 * USER-only members use `/agentic` (Agentic Networks) instead.
 */
export function canAccessNetworkWorkbench(role) {
  const r = normalizeStepflowRole(role);
  return r === 'OWNER' || r === 'ADMIN' || r === 'CREATOR';
}

export function canAccessAccountBilling(role) {
  const r = normalizeStepflowRole(role);
  return r === 'OWNER' || r === 'ADMIN';
}

/** Tenant-scoped usage & billing (GET /usage) — any workspace member with JWT tenant. */
export function canAccessWorkspaceUsage(role) {
  const r = normalizeStepflowRole(role);
  return r === 'OWNER' || r === 'ADMIN' || r === 'CREATOR' || r === 'USER';
}

/** Payer org rollup: OWNER or ADMIN + non-empty org id (Cognito custom:org_id or session orgId from POST /auth/session). */
export function canAccessOrgBilling(role, orgId) {
  const r = normalizeStepflowRole(role);
  if (r !== 'OWNER' && r !== 'ADMIN') return false;
  const o = String(orgId ?? '').trim();
  return o.length > 0;
}

export function canDeleteNetworkRole(role) {
  const r = normalizeStepflowRole(role);
  return r === 'OWNER' || r === 'ADMIN';
}

/** Workspace admin: settings, members & invites — OWNER and ADMIN only (not CREATOR). */
export function canAccessWorkspaceAdmin(role) {
  const r = normalizeStepflowRole(role);
  return r === 'OWNER' || r === 'ADMIN';
}

/**
 * Stripe / subscription gate: only the workspace **OWNER** is the payer-of-record for that tenant.
 * Invited **USER**, **CREATOR**, and **ADMIN** on someone else’s workspace use that workspace’s plan — they are
 * not sent to checkout for that tenant. When the same user switches to a workspace where they are **OWNER**
 * (default or newly created), they must have an active subscription (or org entitlement).
 *
 * @see SubscriptionGate
 */
export function isWorkspaceBillingPayer(role) {
  return normalizeStepflowRole(role) === 'OWNER';
}

// Builder-approval RBAC moved to roomRoles.js (per-room OWNER/BUILDER).
